/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Checkbox, Field, Button, Price } from '@adobe-commerce/elsie/components';
import {
  GiftOptionsViewProps,
  GiftWrappingConfigProps,
  GiftFormDataType,
  ProductGiftOptionsConfig,
} from '@/cart/types';
import { CartModel, Item, Price as PriceType } from '@/cart/data/models';
import { state } from '@/cart/lib/state';
import { StateUpdater, Dispatch, useId } from 'preact/hooks';

interface CheckboxGroupProps {
  className: string;
  view: GiftOptionsViewProps;
  item: Item | ProductGiftOptionsConfig;
  giftOptions: GiftFormDataType;
  disabled: boolean;
  cartData: CartModel | null;
  giftWrappingConfig: GiftWrappingConfigProps[] | [];
  areGiftOptionsVisible: Record<string, boolean>;
  setShowModal: Dispatch<StateUpdater<boolean>>;
  onInputChange: (event: Event) => void;
}

export const CheckboxGroup: FunctionComponent<CheckboxGroupProps> = ({
  className,
  view,
  item,
  giftOptions,
  disabled,
  cartData,
  giftWrappingConfig,
  setShowModal,
  onInputChange,
  areGiftOptionsVisible,
}) => {
  const id = useId();
  const dictionary = useText({
    customize: `Cart.GiftOptions.${view}.customize`,
    giftReceiptIncludedTitle: `Cart.GiftOptions.${view}.giftReceiptIncluded.title`,
    giftReceiptIncludedText: `Cart.GiftOptions.${view}.giftReceiptIncluded.subtitle`,
    printedCardIncludedTitle: `Cart.GiftOptions.${view}.printedCardIncluded.title`,
    printedCardIncludedText: `Cart.GiftOptions.${view}.printedCardIncluded.subtitle`,
    giftOptionsWrapTitle: `Cart.GiftOptions.${view}.giftOptionsWrap.title`,
    giftOptionsWrapText: `Cart.GiftOptions.${view}.giftOptionsWrap.subtitle`,
    requiredFieldError: 'Cart.GiftOptions.formText.requiredFieldError',
  });

  const productGiftWrappingOptions = item?.productGiftWrapping;
  const orderGiftWrappingOptions = cartData?.cartGiftWrapping;
  const giftWrappingOptions =
    view === 'product' ? productGiftWrappingOptions : orderGiftWrappingOptions;

  const selectedGiftWrapping = giftWrappingOptions?.find(
    (el: GiftWrappingConfigProps) => el.uid === giftOptions.giftWrappingId
  );
  const selectedGiftWrappingLabel = selectedGiftWrapping?.design ?? '';

  // Product level configurations
  const productGiftWrappingPrice = item?.giftWrappingPrice;
  const renderSelectedGiftWrappingLabel = selectedGiftWrappingLabel
    ? `${dictionary.giftOptionsWrapText} ${selectedGiftWrappingLabel}`
    : '';

  const printedCardPrice =
    +(state.config?.printedCardPrice?.value ?? 0) > 0
      ? state.config?.printedCardPrice
      : null;

  let productWrappingPrice = null;

  // Product level configurations have higher priority
  if (productGiftWrappingPrice?.value) {
    productWrappingPrice = productGiftWrappingPrice;
  } else if (selectedGiftWrapping?.price?.value) {
    productWrappingPrice = selectedGiftWrapping.price;
  }

  const renderPrice = (price?: PriceType | null) => {
    if (!price) return null;

    return (
      <span>
        &nbsp;(+
        <Price amount={price.value} currency={price.currency} weight="normal" />
        )
      </span>
    );
  };

  return (
    <div
      className={classes([
        className,
        [`${className}--hidden`, !areGiftOptionsVisible.isGiftOptionsVisible],
      ])}
    >
      {areGiftOptionsVisible.isGiftReceiptVisible ? (
        <Field
          disabled={disabled}
          className={'cart-gift-options-view__field-gift-receipt'}
        >
          <Checkbox
            id={`giftReceiptIncluded-${id}`}
            disabled={disabled}
            name={'giftReceiptIncluded'}
            checked={giftOptions.giftReceiptIncluded}
            placeholder={dictionary.giftReceiptIncludedTitle}
            label={dictionary.giftReceiptIncludedTitle}
            description={dictionary.giftReceiptIncludedText}
            onChange={onInputChange}
          />
        </Field>
      ) : null}
      {areGiftOptionsVisible.isPrintedCartVisible ? (
        <Field
          disabled={disabled}
          className={'cart-gift-options-view__field-printed-card'}
        >
          <Checkbox
            id={`printedCardIncluded-${id}`}
            disabled={disabled}
            name={'printedCardIncluded'}
            checked={giftOptions.printedCardIncluded}
            placeholder={dictionary.printedCardIncludedTitle}
            label={
              <>
                {dictionary.printedCardIncludedTitle}
                {renderPrice(printedCardPrice)}
              </>
            }
            description={dictionary.printedCardIncludedText}
            onChange={onInputChange}
          />
        </Field>
      ) : null}
      {areGiftOptionsVisible.isGiftWrappingVisible ? (
        <>
          <Field
            disabled={disabled}
            className={'cart-gift-options-view__field-gift-wrap'}
          >
            <Checkbox
              id={`giftOptionsWrap-${id}`}
              disabled={disabled}
              name={'isGiftWrappingSelected'}
              checked={giftOptions.isGiftWrappingSelected}
              placeholder={dictionary.giftOptionsWrapTitle}
              label={
                <>
                  {dictionary.giftOptionsWrapTitle}
                  {renderPrice(productWrappingPrice)}
                </>
              }
              description={renderSelectedGiftWrappingLabel}
              onChange={onInputChange}
            />
          </Field>

          <Button
            disabled={disabled || !giftWrappingConfig.length}
            type="button"
            data-testid={`gift-option-customize-${view}`}
            variant="tertiary"
            onClick={() => setShowModal(true)}
          >
            {dictionary.customize}
          </Button>
        </>
      ) : null}
    </div>
  );
};
